import { useState, useEffect, useCallback } from 'react';
import { ticketService } from '@/lib/ticketService';
import type { Ticket, TicketReply, TicketStats, TicketStatus, TicketPriority, TicketCategory, TicketAttachment } from '@/components/tickets/types';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';

export function useTicketState() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── LOAD ──────────────────────────────────────────────────────────────────
  const loadTickets = useCallback(async () => {
    if (!currentWorkspace) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [ticketData, statsData] = await Promise.all([
        ticketService.getTicketsByWorkspace(currentWorkspace.id),
        ticketService.getTicketStats(currentWorkspace.id),
      ]);
      setTickets(ticketData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // ─── CREATE ────────────────────────────────────────────────────────────────
  const createTicket = useCallback(async (data: {
    subject: string;
    description: string;
    priority?: TicketPriority;
    category?: TicketCategory;
    clientId?: string | null;
    assignedTo?: string | null;
    attachments?: TicketAttachment[];
    tags?: string[];
  }) => {
    if (!user || !currentWorkspace) return;
    const ticket = await ticketService.createTicket(currentWorkspace.id, user.id, data);
    setTickets(prev => [ticket, ...prev]);
    await loadTickets(); // refresh stats
    return ticket;
  }, [user, currentWorkspace, loadTickets]);

  // ─── UPDATE ────────────────────────────────────────────────────────────────
  const updateTicket = useCallback(async (ticketId: string, updates: Partial<{
    subject: string;
    description: string;
    priority: TicketPriority;
    category: TicketCategory;
    tags: string[];
  }>) => {
    const updated = await ticketService.updateTicket(ticketId, updates);
    setTickets(prev => prev.map(t => t.id === ticketId ? updated : t));
    return updated;
  }, []);

  // ─── STATUS ────────────────────────────────────────────────────────────────
  const changeStatus = useCallback(async (ticketId: string, status: TicketStatus) => {
    if (!user || !currentWorkspace) return;
    const ticket = tickets.find(t => t.id === ticketId);
    const previousStatus = ticket?.status || 'open';
    // Optimistic update
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status } : t));
    await ticketService.changeStatus(ticketId, currentWorkspace.id, user.id, status, previousStatus);
    await loadTickets();
  }, [user, currentWorkspace, tickets, loadTickets]);

  // ─── ASSIGN ────────────────────────────────────────────────────────────────
  const assignTicket = useCallback(async (ticketId: string, assignedTo: string | null) => {
    if (!user || !currentWorkspace) return;
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, assigned_to: assignedTo } : t));
    await ticketService.assignTicket(ticketId, currentWorkspace.id, user.id, assignedTo);
    ticketService.clearCache();
    await loadTickets();
  }, [user, currentWorkspace, loadTickets]);

  // ─── DELETE ────────────────────────────────────────────────────────────────
  const deleteTicket = useCallback(async (ticketId: string) => {
    if (!currentWorkspace) return;
    setTickets(prev => prev.filter(t => t.id !== ticketId));
    await ticketService.deleteTicket(ticketId, currentWorkspace.id);
    await loadTickets();
  }, [currentWorkspace, loadTickets]);

  return {
    tickets,
    stats,
    loading,
    error,
    createTicket,
    updateTicket,
    changeStatus,
    assignTicket,
    deleteTicket,
    reload: loadTickets,
  };
}

// ─── Ticket detail hook (for detail view) ────────────────────────────────────
export function useTicketDetail(ticketId: string | null) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const [ticketData, repliesData] = await Promise.all([
        ticketService.getTicketById(ticketId),
        ticketService.getReplies(ticketId),
      ]);
      setTicket(ticketData);
      setReplies(repliesData);
    } catch (err) {
      console.error('Error loading ticket detail:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (ticketId) loadDetail();
    else {
      setTicket(null);
      setReplies([]);
    }
  }, [ticketId, loadDetail]);

  const addReply = useCallback(async (
    content: string,
    isInternal: boolean,
    attachments: TicketAttachment[] = []
  ) => {
    if (!ticketId || !user || !currentWorkspace) return;
    try {
      setSubmitting(true);
      const reply = await ticketService.addReply(
        ticketId,
        currentWorkspace.id,
        user.id,
        content,
        isInternal,
        attachments
      );
      setReplies(prev => [...prev, reply]);
      // Update ticket updated_at optimistically
      setTicket(prev => prev ? { ...prev, updated_at: new Date().toISOString() } : prev);
    } finally {
      setSubmitting(false);
    }
  }, [ticketId, user, currentWorkspace]);

  const changeStatus = useCallback(async (status: TicketStatus) => {
    if (!ticketId || !user || !currentWorkspace || !ticket) return;
    await ticketService.changeStatus(ticketId, currentWorkspace.id, user.id, status, ticket.status);
    ticketService.clearCache();
    await loadDetail();
  }, [ticketId, user, currentWorkspace, ticket, loadDetail]);

  const assignTicket = useCallback(async (assignedTo: string | null) => {
    if (!ticketId || !user || !currentWorkspace) return;
    await ticketService.assignTicket(ticketId, currentWorkspace.id, user.id, assignedTo);
    ticketService.clearCache();
    await loadDetail();
  }, [ticketId, user, currentWorkspace, loadDetail]);

  const updateReply = useCallback(async (replyId: string, content: string) => {
    if (!ticketId || !user) return;
    try {
      const updated = await ticketService.updateReply(replyId, ticketId, content);
      setReplies(prev => prev.map(r => r.id === replyId ? updated : r));
    } catch (err) {
      console.error('Error updating reply:', err);
      throw err;
    }
  }, [ticketId, user]);

  const deleteReply = useCallback(async (replyId: string) => {
    if (!ticketId || !user) return;
    try {
      await ticketService.deleteReply(replyId, ticketId);
      setReplies(prev => prev.filter(r => r.id !== replyId));
    } catch (err) {
      console.error('Error deleting reply:', err);
      throw err;
    }
  }, [ticketId, user]);

  return {
    ticket,
    replies,
    loading,
    submitting,
    addReply,
    updateReply,
    deleteReply,
    changeStatus,
    assignTicket,
    reload: loadDetail,
  };
}
